import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { WALMART_MARKETPLACE_CHANNEL_ID } from '../../constant';
import { getLocalStorageItem, removeLocalStorageItem } from '../../services/StorageService';
import { validateAuthCode } from '../../store/actions/marketplace.action';

const WALMART_SELLER_CENTER_URL = 'https://seller.walmart.com';

const MARKETPLACE_LABELS = {
    walmart: { name: 'Walmart', channelId: String(WALMART_MARKETPLACE_CHANNEL_ID) },
    etsy: { name: 'Etsy', channelId: '10004' },
    ebay: { name: 'eBay', channelId: '10000' },
    amazon: { name: 'Amazon', channelId: '10001' },
    shopify: { name: 'Shopify', channelId: '10002' },
};

/**
 * Generic Marketplace OAuth Success Page
 *
 * Works for all marketplaces. Reads `marketplace` from URL params to determine
 * which marketplace connected, and handles marketplace-specific logic:
 *
 * - Walmart: saves config via validateAuthCode, supports Flow 2 (redirect back to Seller Center)
 * - Etsy/eBay/others: shows success message
 *
 * URL params (from adapter callback):
 *   - company_seller_id, seller_id, company_id, integration_id, marketplace, flow
 */
const MarketplaceOAuthSuccess = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('loading');
    const [errorMessage, setErrorMessage] = useState('');

    const marketplace = searchParams.get('marketplace') || 'walmart';
    const label = MARKETPLACE_LABELS[marketplace] || { name: marketplace, channelId: '' };

    const isWalmartInitiated = marketplace === 'walmart' && (
        searchParams.get('flow') === 'walmart_initiated'
        || sessionStorage.getItem('walmart_oauth_flow') === 'walmart_initiated'
    );

    useEffect(() => {
        handleSuccess();
    }, []);

    const handleSuccess = async () => {
        if (marketplace === 'walmart') {
            // Walmart: OAuth service saves tokens, success page saves config via validateAuthCode
            await saveWalmartConfig();
        } else {
            // Etsy/eBay: OAuth service saves complete integration record with tokens
            // Config (sender address etc.) will be updated when user edits from config page
            removeLocalStorageItem('redirect');
            setStatus('success');
        }
    };

    const saveWalmartConfig = async () => {
        try {
            const urlCompanySellerId = searchParams.get('company_seller_id');
            const urlSellerId = searchParams.get('seller_id');
            const urlCompanyId = searchParams.get('company_id');
            const urlIntegrationId = searchParams.get('integration_id');

            const formData = JSON.parse(getLocalStorageItem('redirect') || '{}');

            const payload = {
                seller_id: urlSellerId || formData.seller_id || '',
                company_id: urlCompanyId || formData.company_id || '',
                company_seller_id: urlCompanySellerId || formData.company_seller_id || '',
                channel_id: formData.channel_id || label.channelId,
                integration_name: formData.integration_name || label.name,
                integration_id: urlIntegrationId || formData.integration_id || '',
                auth_code: 'OAUTH_COMPLETED',
                refresh_token: formData.refresh_token || '',
                order_quantity: formData.order_quantity || '0',
                send_confirmation_mail: formData.send_confirmation_mail || false,
                active: formData.active !== undefined ? formData.active : true,
                skip_address_pre_verification: formData.skip_address_pre_verification || false,
                include_packing_slips_in_confirmation_email: formData.include_packing_slips_in_confirmation_email || false,
                load_item_dimensions: formData.load_item_dimensions || false,
                auto_refresh: formData.auto_refresh !== undefined ? formData.auto_refresh : true,
                action_type: formData.action_type || 'externally_fulfilled',
                number_of_days_of_orders: formData.number_of_days_of_orders || '14',
                pending_shipment_statuses: formData.pending_shipment_statuses || 'Completed',
                marketplace_dropdown_url: formData.marketplace_dropdown_url || '',
                ignore_weight_checkbox: formData.ignore_weight_checkbox || false,
                ignore_package_weight_checkbox: formData.ignore_package_weight_checkbox || false,
                save_asin_as_sku_checkbox: formData.save_asin_as_sku_checkbox || false,
                redownload_all_orders_on_next_sync_checkbox: formData.redownload_all_orders_on_next_sync_checkbox || false,
                storeName: formData.storeName || `${label.name} Store`,
                accessToken: formData.accessToken || '',
                storeUrl: formData.storeUrl || '',
                senderAddress: formData.senderAddress || {
                    Override_Company_and_Name: { isActive: false, Company: '', Name: '' },
                    Override_Address: { isActive: false, Address_1: '', Address_2: '', City: '', State: '', Zip: '', Country: '' },
                    Override_Phone: { isActive: false, Phone: '' },
                    Override_Email: { isActive: false, Email: '' }
                },
                returnAddress: formData.returnAddress || {
                    Override_Return_To_Address: { isActive: false, Company: '', Name: '', Address_1: '', Address_2: '', City: '', State: '', Zip: '', Country: '', Phone: '', Email: '' }
                },
            };

            const response = await validateAuthCode(payload);

            if (response?.data?.Status === 200 || response?.data?.Entity?.integrationId) {
                removeLocalStorageItem('redirect');

                if (isWalmartInitiated) {
                    sessionStorage.removeItem('walmart_oauth_flow');
                    setStatus('redirecting');
                    window.location.href = WALMART_SELLER_CENTER_URL;
                    return;
                }
                sessionStorage.removeItem('walmart_oauth_flow');
                setStatus('success');
            } else {
                setStatus('error');
                setErrorMessage(response?.data?.Message?.AppStatusDescription || response?.data?.Message || 'Failed to save configuration');
            }
        } catch (error) {
            console.error(`Error saving ${label.name} config:`, error);
            setStatus('error');
            setErrorMessage(error?.response?.data?.Message || error.message || 'Failed to save configuration');
        }
    };

    const handleRedirectionClick = () => {
        removeLocalStorageItem('redirect');
        navigate('/marketplace-integration');
    };

    return (
        <main>
            <div className="main__container">
                <div className="cus-page-content-sec">
                    <div className="top_right_button_container cus-content-only-sec">
                        <div className='incoming_wrapper main_content_table addressDetailWrapper'>
                            {(status === 'loading' || status === 'redirecting') && (
                                <div className='statusRedirect'>
                                    <div className='icon'>
                                        <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                                    </div>
                                    <h2 className='mb-10'>
                                        {status === 'redirecting' ? `Redirecting to ${label.name}...` : 'Saving Configuration...'}
                                    </h2>
                                    <p>
                                        {status === 'redirecting'
                                            ? `Your account has been connected. Taking you back to ${label.name}.`
                                            : 'Please wait while we complete the setup.'}
                                    </p>
                                </div>
                            )}
                            {status === 'success' && (
                                <div className='statusRedirect'>
                                    <div className='icon'>
                                        <i className="fa fa-check" aria-hidden="true"></i>
                                    </div>
                                    <h2 className='mb-10'>{label.name} Account Connected</h2>
                                    <p>Your {label.name} account has been connected successfully.</p>
                                    <button
                                        className="btn cus-primary-transparent-btn"
                                        onClick={handleRedirectionClick}
                                    >
                                        Back to Integration
                                    </button>
                                </div>
                            )}
                            {status === 'error' && (
                                <div className='statusRedirect error'>
                                    <div className='icon error'>
                                        <i className="fa fa-times" aria-hidden="true"></i>
                                    </div>
                                    <h2 className='mb-10'>Configuration Failed</h2>
                                    <p>{errorMessage || `Failed to connect your ${label.name} account. Please try again.`}</p>
                                    <button
                                        className="btn cus-primary-transparent-btn"
                                        onClick={handleRedirectionClick}
                                    >
                                        Back to Integration
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default MarketplaceOAuthSuccess;
